import * as VAConstants from "@constants/virtualAssistanceConstants";
import { GUIDE_VIDEO_NAMES } from "@constants/guideVideoConstants";
import {
  AssistanceVideo,
  TestCategory,
  TestLanguage,
  VirtualAssistantConfig,
} from "types/testTypes";
import { TEST_DEFINITIONS } from "@constants/testConstants/testDefinitions";
import { AVAILABLE_VIRTUAL_ASSISTANT_OPTIONS_ENGLISH_ONLY } from "@constants/testConstants/configurationHelperConstants";
import { SCOPE_TYPE } from "@model";

// Assets whose virtual-assistant video only ships in English even though the
// TestCategory they belong to otherwise supports the full language list —
// e.g. AntiSaccades within OcularMotility, narrowed to
// AVAILABLE_VIRTUAL_ASSISTANT_OPTIONS_ENGLISH_ONLY at config time in
// GetUpdatedAvailableOMConfigsAfterConfigUpdate (OcularMotilityUtils.ts).
// TEST_DEFINITIONS is keyed by category, not by these narrower per-test-type
// video sets, so a download failure for one of these in any other language
// has to be checked against the English-only list, not the category's.
const ENGLISH_ONLY_VA_ASSET_NAMES = new Set<string>([
  VAConstants.antiSaccadesInstructionsVideo01.videoName,
]);

const REQUIRED_FEATURE_SCOPE_BY_ASSET_NAME = new Map<string, string>([
  [VAConstants.antiSaccadesInstructionsVideo01.videoName, SCOPE_TYPE.antiSaccade],
]);

function hasRequiredFeatureScope(assetName: string, scopes: string[]): boolean {
  const requiredFeatureScope = REQUIRED_FEATURE_SCOPE_BY_ASSET_NAME.get(assetName);
  return !requiredFeatureScope || scopes.includes(requiredFeatureScope);
}

export function shouldReportVAAssetFailure(
  assetName: string,
  language: TestLanguage,
  scopes: string[],
): boolean {
  const category = assetName.split("/")[0] as TestCategory;
  const isCategoryAsset = Object.values(TestCategory).includes(category);
  const candidateCategories = isCategoryAsset ? [category] : Object.values(TestCategory);
  const licensedCategories = scopes.filter((scope) =>
    Object.values(TestCategory).includes(scope as TestCategory),
  );
  const relevantCategories =
    licensedCategories.length > 0
      ? candidateCategories.filter((candidate) => licensedCategories.includes(candidate))
      : candidateCategories;

  if (licensedCategories.length > 0 && !hasRequiredFeatureScope(assetName, scopes)) return false;

  const languageChoices = ENGLISH_ONLY_VA_ASSET_NAMES.has(assetName)
    ? AVAILABLE_VIRTUAL_ASSISTANT_OPTIONS_ENGLISH_ONLY.choices
    : null;

  return relevantCategories.some((relevantCategory) =>
    (
      languageChoices ??
      TEST_DEFINITIONS[relevantCategory].availableOptions.virtualAssistantOptions?.choices
    )?.some(({ value }) => value === language),
  );
}

export function isVAAssetNameLicensed(
  assetName: string,
  scopes: string[],
  scopesLoaded: boolean,
): boolean {
  const category = assetName.split("/")[0] as TestCategory;
  const isCategoryAsset = Object.values(TestCategory).includes(category);
  if (!isCategoryAsset) return true;
  return scopesLoaded && scopes.includes(category) && hasRequiredFeatureScope(assetName, scopes);
}

function collectVideoNamesFromVideo(video: AssistanceVideo, collected: Set<string>): void {
  collected.add(video.videoName);
  if (video.onCompleteInteractionFail) {
    collectVideoNamesFromVideo(video.onCompleteInteractionFail, collected);
  }
}

/**
 * Dynamically collects all unique asset names from every exported VirtualAssistantConfig
 * in virtualAssistanceConstants — any new exported config is automatically picked up.
 * Videos that live outside a config (injected dynamically, like the eye-tracking
 * calibration instructions), guide videos and assistant audios must be added explicitly
 * below. Audio names carry their .wav extension, videos are extensionless.
 */
export function getAllVAAssetNames(): string[] {
  const collected = new Set<string>();
  Object.values(VAConstants)
    .filter(
      (val): val is VirtualAssistantConfig =>
        val !== null &&
        typeof val === "object" &&
        !Array.isArray(val) &&
        "videoInfos" in val &&
        Array.isArray((val as any).videoInfos),
    )
    .forEach((config) =>
      config.videoInfos.forEach((video) => collectVideoNamesFromVideo(video, collected)),
    );
  collectVideoNamesFromVideo(VAConstants.eyeTrackingCalibrationInstructionsVideo, collected);
  GUIDE_VIDEO_NAMES.forEach((videoName) => collected.add(videoName));
  VAConstants.VA_AUDIO_NAMES.forEach((audioName) => collected.add(audioName));
  return Array.from(collected);
}
